/**
 * Vendor Approval Inbox — for Purchase Managers and CFO.
 *
 * PM tab: list suppliers awaiting PM approval
 * CFO tab: list suppliers awaiting CFO approval (after PM approval)
 */
import { useState } from 'react'
import {
  Tabs, Table, Tag, Button, Space, Typography, App, Modal, Input, Empty,
  Descriptions, Divider,
} from 'antd'
import {
  CheckOutlined, CloseOutlined, EditOutlined,
  InboxOutlined, DownloadOutlined, ExclamationCircleOutlined,
} from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { suppliersApi } from '@/features/gate/api'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'
import type { Supplier } from '@/shared/types/master'

const { Text } = Typography

const DOC_LABELS: Record<string, string> = {
  pan: 'PAN', gstin: 'GSTIN', cheque: 'Cheque', msme: 'MSME', iso: 'ISO',
}

export default function VendorApprovalInboxPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()

  const canPM  = usePermission('supplier.approve_pm')
  const canCFO = usePermission('supplier.approve_cfo')

  const { data: pendingPM = [], isLoading: loadingPM } = useQuery({
    queryKey: ['suppliers-pending-pm'],
    queryFn: suppliersApi.listPendingPm,
    enabled: canPM,
    refetchInterval: 60_000,
  })

  const { data: pendingCFO = [], isLoading: loadingCFO } = useQuery({
    queryKey: ['suppliers-pending-cfo'],
    queryFn: suppliersApi.listPendingCfo,
    enabled: canCFO,
    refetchInterval: 60_000,
  })

  const [target, setTarget] = useState<{ vendor: Supplier; level: 'PM' | 'CFO'; status: 'APPROVED' | 'REJECTED' | 'CHANGES_REQUESTED' } | null>(null)
  const [remarks, setRemarks] = useState('')
  const [viewVendor, setViewVendor] = useState<Supplier | null>(null)

  const actMutation = useMutation({
    mutationFn: () => {
      const { vendor, level, status } = target!
      return level === 'PM'
        ? suppliersApi.pmAction(vendor.id, { status, remarks })
        : suppliersApi.cfoAction(vendor.id, { status: status as 'APPROVED' | 'REJECTED', remarks })
    },
    onSuccess: () => {
      message.success(
        target?.status === 'APPROVED' ? 'Approved' :
        target?.status === 'REJECTED' ? 'Rejected' : 'Changes requested',
      )
      qc.invalidateQueries({ queryKey: ['suppliers-pending-pm'] })
      qc.invalidateQueries({ queryKey: ['suppliers-pending-cfo'] })
      setTarget(null)
      setRemarks('')
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Action failed'),
  })

  const docsColumn = (v: Supplier) => (
    <Space wrap size={4}>
      {['pan', 'gstin', 'cheque', 'msme', 'iso'].map((dt) => {
        const url = (v as any)[`docs_${dt}_url`]
        return url ? (
          <Button
            key={dt}
            size="small"
            icon={<DownloadOutlined />}
            onClick={() => window.open(suppliersApi.getDocumentUrl(v.id, dt), '_blank')}
            style={{ fontSize: 11 }}
          >
            {DOC_LABELS[dt]}
          </Button>
        ) : null
      })}
    </Space>
  )

  const renderColumns = (level: 'PM' | 'CFO') => [
    { title: 'Code', dataIndex: 'supplier_code', key: 'code', width: 90 },
    { title: 'Name', dataIndex: 'supplier_name', key: 'name' },
    {
      title: 'Type', dataIndex: 'supplier_type', key: 'type',
      render: (v: string) => <Tag>{v.replace(/_/g, ' ')}</Tag>,
    },
    {
      title: 'GSTIN / PAN', key: 'tax',
      render: (_: unknown, v: Supplier) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontFamily: 'monospace', fontSize: 11 }}>{v.gstin ?? '—'}</Text>
          <Text type="secondary" style={{ fontFamily: 'monospace', fontSize: 11 }}>{v.pan ?? '—'}</Text>
        </Space>
      ),
    },
    {
      title: 'Bank', key: 'bank',
      render: (_: unknown, v: Supplier) => (
        <Space direction="vertical" size={0} style={{ fontSize: 11 }}>
          <Text>{v.bank_name ?? '—'}</Text>
          <Text type="secondary" style={{ fontFamily: 'monospace' }}>{v.ifsc_code ?? '—'}</Text>
        </Space>
      ),
    },
    {
      title: 'Expected Annual', dataIndex: 'expected_annual_value', key: 'eav',
      render: (v?: string) => v ? `₹${Number(v).toLocaleString('en-IN')}` : '—',
    },
    { title: 'Documents', key: 'docs', render: (_: unknown, v: Supplier) => docsColumn(v) },
    {
      title: 'Action', key: 'action',
      render: (_: unknown, v: Supplier) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => setViewVendor(v)}>View</Button>
          <Button
            size="small" type="primary" icon={<CheckOutlined />}
            onClick={() => { setTarget({ vendor: v, level, status: 'APPROVED' }); setRemarks('') }}
          >
            Approve
          </Button>
          {level === 'PM' && (
            <Button
              size="small" icon={<ExclamationCircleOutlined />}
              onClick={() => { setTarget({ vendor: v, level, status: 'CHANGES_REQUESTED' }); setRemarks('') }}
            >
              Request Changes
            </Button>
          )}
          <Button
            size="small" danger icon={<CloseOutlined />}
            onClick={() => { setTarget({ vendor: v, level, status: 'REJECTED' }); setRemarks('') }}
          >
            Reject
          </Button>
        </Space>
      ),
    },
  ]

  const tabs = []
  if (canPM) {
    tabs.push({
      key: 'pm',
      label: (
        <span>
          <InboxOutlined /> Pending PM Approval
          {pendingPM.length > 0 && <Tag color="orange" style={{ marginLeft: 8 }}>{pendingPM.length}</Tag>}
        </span>
      ),
      children: (
        <Table
          rowKey="id"
          columns={renderColumns('PM')}
          dataSource={pendingPM}
          loading={loadingPM}
          size="small"
          locale={{ emptyText: <Empty description="No pending PM approvals" /> }}
          scroll={{ x: 1200 }}
        />
      ),
    })
  }
  if (canCFO) {
    tabs.push({
      key: 'cfo',
      label: (
        <span>
          Pending CFO Approval
          {pendingCFO.length > 0 && <Tag color="gold" style={{ marginLeft: 8 }}>{pendingCFO.length}</Tag>}
        </span>
      ),
      children: (
        <Table
          rowKey="id"
          columns={renderColumns('CFO')}
          dataSource={pendingCFO}
          loading={loadingCFO}
          size="small"
          locale={{ emptyText: <Empty description="No pending CFO approvals" /> }}
          scroll={{ x: 1200 }}
        />
      ),
    })
  }

  return (
    <div>
      <PageHeader
        title="Vendor Approval Inbox"
        subtitle="Review new vendor registrations awaiting your approval"
      />
      {tabs.length === 0 && (
        <Empty description="You don't have approval permissions for vendors" />
      )}
      {tabs.length > 0 && <Tabs items={tabs} />}

      {/* ── Action Modal ─────────────────────────────────────────────────── */}
      <Modal
        title={
          target ? (
            target.status === 'APPROVED' ? `Approve ${target.vendor.supplier_name}` :
            target.status === 'REJECTED' ? `Reject ${target.vendor.supplier_name}` :
            `Request Changes for ${target.vendor.supplier_name}`
          ) : ''
        }
        open={!!target}
        onCancel={() => setTarget(null)}
        onOk={() => actMutation.mutate()}
        confirmLoading={actMutation.isPending}
        okButtonProps={{ danger: target?.status === 'REJECTED' }}
      >
        {target?.level === 'PM' && target.status === 'APPROVED' && (
          <div style={{ padding: 8, background: '#e6f4ff', borderRadius: 6, marginBottom: 12 }}>
            <Text style={{ fontSize: 12 }}>
              ℹ Vendor will be routed to CFO for final approval (or activated directly if below threshold).
            </Text>
          </div>
        )}
        {target?.status === 'REJECTED' && (
          <Text type="danger" style={{ display: 'block', marginBottom: 8 }}>
            ⚠ Rejection is final — vendor cannot be resubmitted.
          </Text>
        )}
        <Input.TextArea
          rows={4}
          placeholder={
            target?.status === 'APPROVED'  ? 'Optional approval remarks…'  :
            target?.status === 'REJECTED'  ? 'Reason for rejection (required)…' :
            'What needs to change? (required)…'
          }
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          maxLength={500}
          showCount
        />
      </Modal>

      {/* ── Vendor Detail View ───────────────────────────────────────────── */}
      <Modal
        title={viewVendor ? `Vendor Details: ${viewVendor.supplier_name}` : ''}
        open={!!viewVendor}
        onCancel={() => setViewVendor(null)}
        footer={null}
        width={720}
      >
        {viewVendor && (
          <div>
            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label="Code">{viewVendor.supplier_code}</Descriptions.Item>
              <Descriptions.Item label="Type">{viewVendor.supplier_type.replace(/_/g, ' ')}</Descriptions.Item>
              <Descriptions.Item label="Contact">{viewVendor.contact_person ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Phone">{viewVendor.phone ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Email" span={2}>{viewVendor.email ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Address" span={2}>{viewVendor.address ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="City">{viewVendor.city ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="State">{viewVendor.state ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="GSTIN">
                <span style={{ fontFamily: 'monospace' }}>{viewVendor.gstin ?? '—'}</span>
                {viewVendor.gstin_verified && <Tag color="green" style={{ marginLeft: 8 }}>✓ Format OK</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="PAN">
                <span style={{ fontFamily: 'monospace' }}>{viewVendor.pan ?? '—'}</span>
              </Descriptions.Item>
              <Descriptions.Item label="MSME No.">{viewVendor.msme_reg_no ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Expected Annual">
                {viewVendor.expected_annual_value
                  ? `₹${Number(viewVendor.expected_annual_value).toLocaleString('en-IN')}`
                  : '—'}
              </Descriptions.Item>
            </Descriptions>

            <Divider orientation="left">Bank</Divider>
            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label="Bank">{viewVendor.bank_name ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Branch">{viewVendor.bank_branch ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="A/C No">{viewVendor.account_number ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="IFSC">
                <span style={{ fontFamily: 'monospace' }}>{viewVendor.ifsc_code ?? '—'}</span>
              </Descriptions.Item>
            </Descriptions>

            <Divider orientation="left">Documents</Divider>
            <Space wrap>
              {['pan', 'gstin', 'cheque', 'msme', 'iso'].map((dt) => {
                const url = (viewVendor as any)[`docs_${dt}_url`]
                return url ? (
                  <Button
                    key={dt}
                    icon={<DownloadOutlined />}
                    onClick={() => window.open(suppliersApi.getDocumentUrl(viewVendor.id, dt), '_blank')}
                  >
                    View {DOC_LABELS[dt]}
                  </Button>
                ) : (
                  <Tag key={dt} color="default">{DOC_LABELS[dt]}: Not uploaded</Tag>
                )
              })}
            </Space>
          </div>
        )}
      </Modal>
    </div>
  )
}
